import type { RoutineTask, TaskSubType, CreateProjectRoutineTask } from '../types/RoutineTask';
import { Activity } from '../types/Activity';
import { convertTaskDates } from '../utils/dateUtils';

const BASE_URL = '/api/tasks/routine-task';
const ROUTINE_MODULE_URL = '/api/routine';

// Add new interfaces for pagination and filtering
export interface RoutineTaskListResponse {
  data: RoutineTask[];
  pagination: {
    page: number;
    pageSize: number;
    totalCount: number;
    totalPages: number;
    hasNext: boolean;
    hasPrevious: boolean;
  };
}

export interface RoutineTaskFilters {
  foremanArea?: string | string[];
  status?: string | string[];
  /** Preferred activity filter — stable ids; never contains commas. */
  activityNumber?: string | string[];
  /** @deprecated Prefer activityNumber. Kept for older bookmarked URLs. */
  activityName?: string | string[];
  priority?: string;
  startDate?: string;
  endDate?: string;
  search?: string;
  serviceArea?: string;
  /** When set, list returns only this task (e.g. from Work ID screen link). */
  taskId?: string;
}

export interface RoutineTaskFilterOptions {
  statuses: string[];
  priorities: string[];
  foremanAreas: Array<{
    id: number;
    name: string;
    serviceAreaId?: number | null;
    serviceAreaName?: string | null;
  }>;
  activities: Array<{ id: number; activityName: string; activityNumber: string; }>;
  serviceAreas: Array<{ id: number; name: string; }>;
}

// Old DTO for standing tasks
export interface CreateStandingRoutineTaskDTO {
  id?: number;
  name?: string;
  description: string;
  projectId?: number;
  activities: number[];
  status: string;
  mode: 'standing' | 'specific';
  taskSubType?: TaskSubType;
  labourAllocation?: number;
  materialAllocation?: number;
  equipmentAllocation?: number;
  secondaryLabourAllocation?: number;
  secondaryMaterialAllocation?: number;
  secondaryEquipmentAllocation?: number;
  foremanAreaId?: number;
  type?: string;
}

export interface CreateRoutineTaskDTO {
  id?: number;
  taskId?: number;
  description: string;
  projectId?: number;
  activities?: Activity[];
  status: string;
  mode: 'standing' | 'specific';  // Allow both modes
  taskSubType?: TaskSubType;
  labourAllocation?: number;
  materialAllocation?: number;
  equipmentAllocation?: number;
  secondaryLabourAllocation?: number;
  secondaryMaterialAllocation?: number;
  secondaryEquipmentAllocation?: number;
  internalNotes?: string;
  instructions?: string;
  priority: string;
  taskLocations?: Array<{
    type: 'START' | 'END';
    latitude: number;
    longitude: number;
    assetId?: number;
  }>;
  contractAreaId?: number;
  assignedEmployeeIds?: number[];
}

export const routineTaskService = {
  /**
   * Get routine tasks with server-side pagination and filtering
   */
  getRoutineTasksPaginated: async (
    page: number = 1, 
    pageSize: number = 10, 
    filters: RoutineTaskFilters = {},
    mode: string = 'specific',
    sort?: { sortBy: string; sortOrder: 'asc' | 'desc' }
  ): Promise<RoutineTaskListResponse> => {
    try {
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('pageSize', pageSize.toString());
      params.append('mode', mode);
      if (sort?.sortBy) {
        params.append('sortBy', sort.sortBy);
        params.append('sortOrder', sort.sortOrder === 'desc' ? 'desc' : 'asc');
      }
      
      // Handle filter parameters, properly serializing arrays
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== '' && !(Array.isArray(value) && value.length === 0)) {
          if (Array.isArray(value)) {
            // For arrays, append each value with the same key
            value.forEach(v => {
              if (v !== '') {
                params.append(key, v.toString());
              }
            });
          } else {
            params.append(key, value.toString());
          }
        }
      });

      const response = await fetch(`${ROUTINE_MODULE_URL}?${params}`);
      if (!response.ok) {
        throw new Error('Failed to fetch routine tasks');
      }
      const data = await response.json();
      
      // Convert date strings to Date objects for all tasks
      if (data.data && Array.isArray(data.data)) {
        data.data = data.data.map((task: RoutineTask) => convertTaskDates(task));
      }
      
      return data;
    } catch (error) {
      console.error('Error fetching paginated routine tasks:', error);
      throw error;
    }
  },

  /**
   * Get filter options for routine tasks
   */
  getRoutineTaskFilterOptions: async (): Promise<RoutineTaskFilterOptions> => {
    try {
      const response = await fetch(`${ROUTINE_MODULE_URL}/filter-options`);
      if (!response.ok) {
        throw new Error('Failed to fetch filter options');
      }
      return response.json();
    } catch (error) {
      console.error('Error fetching filter options:', error);
      throw error;
    }
  },

  /**
   * Get all routine tasks
   */
  getAll: async (): Promise<RoutineTask[]> => {
    try {
      const response = await fetch(BASE_URL);
      if (!response.ok) {
        throw new Error('Failed to fetch routine tasks');
      }
      const data = await response.json();
      
      // Convert date strings to Date objects for all tasks
      if (Array.isArray(data)) {
        return data.map((task: RoutineTask) => convertTaskDates(task));
      }
      
      return data;
    } catch (error) {
      console.error('Error fetching routine tasks:', error);
      throw error;
    }
  },

  /**
   * Create a new routine task
   */
  create: async (data: CreateStandingRoutineTaskDTO | CreateProjectRoutineTask): Promise<RoutineTask> => {
    const response = await fetch(BASE_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      throw new Error('Failed to create routine task');
    }
    return response.json();
  },

  /**
   * Update an existing routine task
   */
  update: async (taskId: number, data: CreateStandingRoutineTaskDTO | CreateProjectRoutineTask): Promise<RoutineTask> => {
    const response = await fetch(`${BASE_URL}/update/${taskId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      throw new Error('Failed to update routine task');
    }
    return response.json();
  },

  getTasksByModeAndType: async (mode: string, type: string): Promise<RoutineTask[]> => {
    try {
      // `credentials: 'include'` matches every other authenticated call in this service. It is
      // a no-op while the API is same-origin (fetch defaults to `same-origin`), but this call
      // populates the Base Task dropdown when creating a routine task, and if it ever 401s the
      // symptom is an empty dropdown with no error — a control that looks broken rather than
      // unauthorized. Being explicit removes that as a possible cause.
      const response = await fetch(`${BASE_URL}/task/mode/${mode}/type/${type}`, {
        credentials: 'include',
      });
      if (!response.ok) {
        throw new Error(`Failed to fetch tasks by mode and type (${response.status})`);
      }
      return response.json();
    } catch (error) {
      console.error('Error fetching tasks by mode and type:', error);
      throw error;
    }
  },

  getTaskDetails: async (taskId: number): Promise<RoutineTask> => {
    const response = await fetch(`${BASE_URL}/${taskId}`, { credentials: 'include' });
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      const msg = (body as { error?: string })?.error ?? body?.message ?? `HTTP ${response.status}`;
      throw new Error(`Failed to fetch task details: ${msg}`);
    }
    return response.json();
  },

  /**
   * Update a routine task with the new endpoint
   */
  updateRoutineTask: async (taskId: number, data: CreateProjectRoutineTask): Promise<RoutineTask> => {
    const response = await fetch(`${BASE_URL}/update/${taskId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to update routine task');
    }
    
    return response.json();
  },

  /**
   * Delete a routine task
   */
  delete: async (taskId: number): Promise<void> => {
    const response = await fetch(`${BASE_URL}/${taskId}`, {
      method: 'DELETE',
    });
    if (!response.ok) {
      throw new Error('Failed to delete routine task');
    }
  },

  updateTaskStatus: async (taskId: number, status: string, comments?: string): Promise<RoutineTask> => {
    const response = await fetch(`${BASE_URL}/${taskId}/status`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ status, ...(comments != null && comments.trim() && { comments: comments.trim() }) }),
      credentials: 'include',
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const detail = errorData?.details || errorData?.message;
      const main = errorData?.error || `Failed to update task status (${response.status})`;
      const message = detail ? `${main}: ${detail}` : main;
      throw new Error(message);
    }

    return response.json();
  },

  /**
   * Add a history comment to a routine task
   */
  addHistoryComment: async (taskId: number, data: { comments: string; userId: number; isExternal?: boolean }): Promise<RoutineTask> => {
    const response = await fetch(`${BASE_URL}/${taskId}/history-comment`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || 'Failed to add history comment');
    }

    return response.json();
  },
};

export default routineTaskService; 